import React, { useState, forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { Link } from '@reach/router';
import { determineUserTier, truncate, presentErrorBasedOnCode } from 'utils';
import { User, AdminStatus } from 'state/user/UserTypes';
import Label from './Label';
import useViewport from 'hooks/useViewport';
import { updateUser } from 'state/user/UserActions';
import { Error, ErrorCodes } from 'types';
import ErrorMessage from './ErrorMessage';
import { useSelector } from 'react-redux';
import { AppState } from 'state/store';
import { selectCurrentUser } from 'state/user/UserSelectors';
import SmallLoader from './SmallLoader';
import AdminToggleButton from './AdminToggleButton';

interface Props {
   user: User;
   updateAdminList: (admin: User) => void;
}

const ListItemAdmin = forwardRef(({ user, updateAdminList }: Props, ref: Ref<HTMLDivElement>): JSX.Element => {
   const [togglingStatus, setTogglingStatus] = useState<boolean>(false);
   const [error, setError] = useState<Error | null>(null);
   const [{ width: windowWidth }] = useViewport();
   const currentUser = useSelector((state: AppState) => selectCurrentUser(state));

   const toggleAdminStatus = async (): Promise<void> => {
      try {
         await setTogglingStatus(true);
         const updatedAdmin = await updateUser({
            adminStatus: user.adminStatus === AdminStatus.active ? AdminStatus.disabled : AdminStatus.active,
         }, user._id);
         updateAdminList(updatedAdmin);
      } catch (error) {
         error = error.json ? await error.json() : error;
         console.log(error);
         setError(error);
      }
      setTogglingStatus(false);
   };

   return (
      <>
         <Container ref={ref}>
            <ItemInfo>
               <span>
                  <Label>Email</Label>
                  {windowWidth > 590 ? user.email : truncate(user.email, 26)}
               </span>
               <span>
                  <Label>Name</Label>
                  {user.firstName} {user.lastName || ''}
               </span>
               {user.phone && (
                  <span>
                     <Label>Phone</Label>
                     {user.phone}
                  </span>
               )}
               <span>
                  <Label>Tier</Label>
                  {determineUserTier(user)}
               </span>
            </ItemInfo>
            {currentUser?._id !== user._id && <ToggleStatusButton user={user} togglingStatus={togglingStatus} toggle={toggleAdminStatus} />}
            <DetailsLink to={`${window.location.pathname}/${user._id}`}>Details</DetailsLink>
         </Container>
         {error && <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes)}</ErrorMessage>}
      </>
   );
});

const ToggleStatusButton: React.FC<{ user: User; togglingStatus: boolean; toggle: () => void }> = ({ user, togglingStatus, toggle }): JSX.Element => (
   <ToggleButton onClick={toggle}>
      {user.adminStatus === AdminStatus.active ? (
         togglingStatus ? (
           <SmallLoader />
         ) : (
            'Disable'
         )
      ) : togglingStatus ? (
         <SmallLoader />
      ) : (
         'Enable'
      )}
   </ToggleButton>
);

export default ListItemAdmin;

const Container = styled.div`
   display: flex;
   flex-wrap: wrap;
   justify-content: space-between;
   flex-direction: row nowrap;
   padding: 12px 20px;
   position: relative;
   margin: 0 auto;
   background-color: ${({ theme }) => theme.secondaryWhite};
   align-items: center;
   border-radius: 5px;
   border: 1px solid ${({ theme }) => theme.primaryGrey};
   position: relative;
   :not(:last-child) {
      margin-bottom: 10px;
   }
`;

const ItemInfo = styled.div`
   display: flex;
   flex-wrap: wrap;
   flex-direction: column;
   justify-content: center;
   align-items: flex-start;

   @media only screen and (min-width: 1000px) {
      flex-direction: row;
      padding: 5px;
      align-items: center;
      > * {
         margin: 0 15px;
         padding: 5px;
      }
   }
`;

const DetailsLink = styled(Link)`
   padding-top: 3px;
   font-weight: 600;

   @media only screen and (max-width: 415px) {
      margin: 0 0 30px auto;
   }
`;

const ToggleButton = styled(AdminToggleButton)`
   position: absolute;
   bottom: 4px;
   right: 20px;
`;
